if (!customElements.get('skafe-facets-layout')) {
  const STORAGE_KEY = 'skafe:facets-collapsed';
  const HTML_CLASS = 'skafe-facets-collapsed';
  const MOBILE_MQ = '(max-width: 749px)';
  const SORT_GAP_PX = 8;

  // No stored preference means the section's own default wins -- the catalog
  // opens with the rail closed so the grid gets the full width, while the store
  // keeps its filters in view. Once a visitor toggles it, their choice is
  // stored either way and outranks the default on every page.
  const readCollapsed = (fallback) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === null) return Boolean(fallback);
      return stored === '1';
    } catch {
      return document.documentElement.classList.contains(HTML_CLASS);
    }
  };

  const writeCollapsed = (collapsed) => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* private mode */
    }
  };

  const isMobileFacets = () => window.matchMedia(MOBILE_MQ).matches;

  const headerOffsetPx = () => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) ? n : 70;
  };

  class SkafeFacetsLayout extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.toggle = this.querySelector('[data-skafe-facets-toggle]');
      this.panel = this.querySelector('[data-skafe-facets-panel]');
      this.rail = this.querySelector('.skafe-facets-rail');
      this.sortBar =
        this.previousElementSibling?.classList?.contains('facets-vertical-sort')
          ? this.previousElementSibling
          : null;

      this.mobileQuery = window.matchMedia(MOBILE_MQ);
      this.defaultCollapsed = this.dataset.defaultCollapsed === 'true';
      if (this.toggle && this.panel) {
        this.applyCollapsed(readCollapsed(this.defaultCollapsed), { announce: false });
        this.toggle.addEventListener('click', () => this.setCollapsed(!this.isCollapsed()), { signal });
      }
      this.setupPinTracking(signal);

      const onViewportChange = () => {
        if (this.toggle && this.panel) {
          this.applyCollapsed(readCollapsed(this.defaultCollapsed), { announce: false });
        }
        this.setupPinTracking(signal);
      };
      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', onViewportChange, { signal });
      } else {
        this.mobileQuery.addListener(onViewportChange);
        signal.addEventListener('abort', () => this.mobileQuery.removeListener(onViewportChange));
      }

      document.addEventListener(
        'keydown',
        (event) => {
          if (!this.toggle || event.key !== 'Escape' || isMobileFacets() || !this.isCollapsed()) return;
          if (!this.contains(document.activeElement)) return;
          this.setCollapsed(false);
          this.toggle.focus();
        },
        { signal },
      );
    }

    disconnectedCallback() {
      this.pinObserver?.disconnect();
      this.pinObserver = null;
      this.sortPinObserver?.disconnect();
      this.sortPinObserver = null;
      this.sortResize?.disconnect();
      this.sortResize = null;
      this.pinScroll?.abort();
      this.pinScroll = null;
      document.documentElement.style.removeProperty('--skafe-sort-bar-offset');
      this.abortController?.abort();
      this.abortController = null;
    }

    isCollapsed() {
      return this.classList.contains('is-collapsed');
    }

    setCollapsed(collapsed) {
      if (isMobileFacets() || !this.toggle || !this.panel) return;
      this.applyCollapsed(collapsed, { announce: true });
      writeCollapsed(collapsed);
      this.syncPinnedClass();
    }

    applyCollapsed(collapsed, { announce }) {
      if (!this.toggle || !this.panel) return;
      const mobile = isMobileFacets();
      const effective = mobile ? false : collapsed;

      this.classList.toggle('is-collapsed', effective);
      document.documentElement.classList.toggle(HTML_CLASS, effective);

      const expandedLabel = this.toggle.querySelector('[data-label-hide]')?.textContent?.trim() || '';
      const collapsedLabel = this.toggle.querySelector('[data-label-show]')?.textContent?.trim() || '';
      this.toggle.setAttribute('aria-expanded', effective ? 'false' : 'true');
      this.toggle.setAttribute('aria-label', effective ? collapsedLabel : expandedLabel);
      this.panel.toggleAttribute('inert', effective);
      this.panel.setAttribute('aria-hidden', effective ? 'true' : 'false');

      const hideLabel = this.toggle.querySelector('[data-label-hide]');
      const showLabel = this.toggle.querySelector('[data-label-show]');
      if (hideLabel) hideLabel.hidden = effective;
      if (showLabel) showLabel.hidden = !effective;

      if (announce) {
        const live = this.querySelector('[data-skafe-facets-live]');
        if (live) {
          live.textContent = effective
            ? this.toggle.dataset.announceCollapsed || ''
            : this.toggle.dataset.announceExpanded || '';
        }
      }
    }

    updateSortBarOffset() {
      if (isMobileFacets() || !this.sortBar) {
        document.documentElement.style.removeProperty('--skafe-sort-bar-offset');
        return;
      }
      const height = Math.ceil(this.sortBar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--skafe-sort-bar-offset', `${height + SORT_GAP_PX}px`);
    }

    setupPinTracking(signal) {
      this.pinObserver?.disconnect();
      this.pinObserver = null;
      this.sortPinObserver?.disconnect();
      this.sortPinObserver = null;
      this.sortResize?.disconnect();
      this.sortResize = null;
      this.pinScroll?.abort();
      this.pinScroll = null;

      const mobile = isMobileFacets();

      if (mobile) {
        this.sortBar?.classList.remove('is-pinned');
        document.documentElement.style.removeProperty('--skafe-sort-bar-offset');
      } else {
        this.updateSortBarOffset();
        if (this.sortBar && typeof ResizeObserver !== 'undefined') {
          this.sortResize = new ResizeObserver(() => this.updateSortBarOffset());
          this.sortResize.observe(this.sortBar);
        }

        if (this.sortBar) {
          let sortSentinel = this.parentElement?.querySelector(':scope > .facets-vertical-sort-sentinel');
          if (!sortSentinel) {
            sortSentinel = document.createElement('div');
            sortSentinel.className = 'facets-vertical-sort-sentinel';
            sortSentinel.setAttribute('aria-hidden', 'true');
            this.sortBar.before(sortSentinel);
          }
          const sortTop = Math.max(0, Math.round(headerOffsetPx()));
          this.sortPinObserver = new IntersectionObserver(
            ([entry]) => {
              this._sortSentinelVisible = entry?.isIntersecting ?? true;
              this.syncPinnedClass();
            },
            { threshold: 0, rootMargin: `-${sortTop}px 0px 0px 0px` },
          );
          this.sortPinObserver.observe(sortSentinel);
        }
      }

      if (this.rail) {
        let sentinel = this.querySelector('.skafe-facets-rail-sentinel');
        if (!sentinel) {
          sentinel = document.createElement('div');
          sentinel.className = 'skafe-facets-rail-sentinel';
          sentinel.setAttribute('aria-hidden', 'true');
          this.rail.before(sentinel);
        }

        const sortOffset = mobile || !this.sortBar ? 0 : this.sortBar.offsetHeight + SORT_GAP_PX;
        const top = Math.max(0, Math.round(headerOffsetPx() + sortOffset + (mobile ? 0 : 12)));
        this.pinObserver = new IntersectionObserver(
          ([entry]) => {
            this._sentinelVisible = entry?.isIntersecting ?? true;
            this.syncPinnedClass();
          },
          { threshold: 0, rootMargin: `-${top}px 0px 0px 0px` },
        );
        this.pinObserver.observe(sentinel);
      }

      this.pinScroll = new AbortController();
      const pinSignal = this.pinScroll.signal;
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this.syncPinnedClass();
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true, signal: pinSignal });
      signal.addEventListener('abort', () => {
        this.pinObserver?.disconnect();
        this.sortPinObserver?.disconnect();
        this.sortResize?.disconnect();
        this.pinScroll?.abort();
      });
      this.syncPinnedClass();
    }

    syncPinnedClass() {
      const mobile = isMobileFacets();

      if (mobile) {
        this.sortBar?.classList.remove('is-pinned');
        if (!this.rail) return;
        const stickyTop = Number.parseFloat(getComputedStyle(this.rail).top) || headerOffsetPx();
        const top = this.rail.getBoundingClientRect().top;
        const pinned = this._sentinelVisible === false && top <= stickyTop + 8;
        this.rail.classList.toggle('is-pinned', pinned);
        return;
      }

      if (this.sortBar) {
        const stickyTop = Number.parseFloat(getComputedStyle(this.sortBar).top) || headerOffsetPx();
        const top = this.sortBar.getBoundingClientRect().top;
        const pinned = this._sortSentinelVisible === false && top <= stickyTop + 8;
        this.sortBar.classList.toggle('is-pinned', pinned);
      }

      if (!this.rail) return;
      if (this.isCollapsed()) {
        this.rail.classList.remove('is-pinned');
        return;
      }
      const stickyTop = Number.parseFloat(getComputedStyle(this.rail).top) || headerOffsetPx() + 12;
      const top = this.rail.getBoundingClientRect().top;
      const pinned = this._sentinelVisible === false && top <= stickyTop + 8;
      this.rail.classList.toggle('is-pinned', pinned);
    }
  }

  customElements.define('skafe-facets-layout', SkafeFacetsLayout);
}
